import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@hooks/useAuth';
import useOnboarding from '@hooks/useOnboarding';
import { accountService } from '@services/accountService';
import { categoryService } from '@services/categoryService';
import { transactionService } from '@services/transactionService';
import Input from '@components/common/Input';
import Select from '@components/common/Select';
import CurrencyInput from '@components/common/CurrencyInput';
import Button from '@components/common/Button';
import Spinner from '@components/common/Spinner';
import { ACCOUNT_TYPE_LABELS, COLORS } from '@utils/constants';
import { formatCurrency } from '@utils/formatters';
import { clsx } from 'clsx';
import {
  Wallet,
  Tag,
  Receipt,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Check,
  ArrowRight,
  Rocket,
} from 'lucide-react';
import toast from 'react-hot-toast';

const STEPS = [
  { id: 'welcome', label: 'Bem-vindo', icon: Sparkles },
  { id: 'accounts', label: 'Contas', icon: Wallet },
  { id: 'categories', label: 'Categorias', icon: Tag },
  { id: 'transaction', label: 'Transacao', icon: Receipt },
  { id: 'done', label: 'Pronto', icon: Rocket },
];

const accountTypeOptions = Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const icons = ['🏦', '💳', '💰', '📈', '💵', '🏧', '💜', '🧡', '💛', '💚', '💙', '🖤'];

const accountSchema = z.object({
  name: z.string().min(2, 'Nome deve ter no minimo 2 caracteres'),
  type: z.string().min(1, 'Tipo e obrigatorio'),
  initialBalance: z.number().default(0),
  color: z.string().default('#3B82F6'),
  icon: z.string().default('🏦'),
});

const transactionSchema = z.object({
  description: z.string().min(2, 'Descricao deve ter no minimo 2 caracteres'),
  amount: z.number().min(0.01, 'Valor deve ser maior que zero'),
  type: z.enum(['income', 'expense']),
  accountId: z.string().min(1, 'Selecione uma conta'),
  categoryId: z.string().min(1, 'Selecione uma categoria'),
  date: z.string().min(1, 'Data e obrigatoria'),
});

// --- Step Components ---

const WelcomeStep = ({ user, onNext }) => (
  <div className="flex flex-col items-center text-center max-w-lg mx-auto">
    <div className="w-20 h-20 rounded-2xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-6">
      <Sparkles size={40} className="text-primary-600" />
    </div>

    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
      Bem-vindo ao MyBank{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
    </h2>

    <p className="text-gray-500 dark:text-gray-400 text-lg mb-8">
      Vamos configurar sua conta em poucos passos para que voce possa comecar
      a controlar suas financas agora mesmo.
    </p>

    <div className="w-full space-y-4 mb-8">
      {[
        { icon: Wallet, label: 'Cadastre suas contas', desc: 'Bancos, carteiras, cartoes.' },
        { icon: Tag, label: 'Configure categorias', desc: 'Organize suas receitas e despesas.' },
        { icon: Receipt, label: 'Registre seu primeiro gasto', desc: 'Comece o controle financeiro.' },
      ].map(({ icon: Icon, label, desc }, i) => (
        <div
          key={i}
          className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 text-left"
        >
          <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
            <Icon size={20} className="text-primary-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">{label}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
          </div>
        </div>
      ))}
    </div>

    <Button size="lg" onClick={onNext} rightIcon={<ArrowRight size={18} />}>
      Comecar configuracao
    </Button>
  </div>
);

const AccountStep = ({ accounts, onAddAccount, isAdding, onNext, onBack }) => {
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      name: '',
      type: 'checking',
      initialBalance: 0,
      color: '#3B82F6',
      icon: '🏦',
    },
  });

  const selectedColor = watch('color');
  const selectedIcon = watch('icon');

  const handleAdd = async (data) => {
    await onAddAccount(data);
    reset();
  };

  return (
    <div className="max-w-2xl mx-auto w-full">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mx-auto mb-4">
          <Wallet size={28} className="text-primary-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Cadastre suas contas
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          Adicione pelo menos uma conta bancaria ou carteira para comecar.
        </p>
      </div>

      {/* Added accounts */}
      {accounts.length > 0 && (
        <div className="mb-6 space-y-2">
          {accounts.map((acc) => (
            <div
              key={acc.accountId}
              className="flex items-center gap-3 p-3 rounded-xl bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800"
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center text-lg"
                style={{ backgroundColor: `${acc.color}18` }}
              >
                {acc.icon}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 dark:text-white text-sm">{acc.name}</p>
                <p className="text-xs text-gray-500">{ACCOUNT_TYPE_LABELS[acc.type]}</p>
              </div>
              <div className="flex items-center gap-1.5 text-green-600">
                <Check size={16} />
                <span className="text-sm font-medium">{formatCurrency(acc.initialBalance || acc.currentBalance || 0)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit(handleAdd)} className="space-y-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Nome da conta"
            placeholder="Ex: Nubank, Inter, etc"
            error={errors.name?.message}
            {...register('name')}
          />
          <Select
            label="Tipo de conta"
            options={accountTypeOptions}
            error={errors.type?.message}
            {...register('type')}
          />
        </div>

        <Controller
          name="initialBalance"
          control={control}
          render={({ field }) => (
            <CurrencyInput
              label="Saldo atual"
              value={field.value}
              onValueChange={(values) => field.onChange(values.floatValue ?? 0)}
              onBlur={field.onBlur}
              allowNegative
              error={errors.initialBalance?.message}
              hint="Informe o saldo atual da conta"
            />
          )}
        />

        <div className="flex gap-6">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Icone
            </label>
            <div className="flex flex-wrap gap-1.5">
              {icons.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  onClick={() => setValue('icon', icon)}
                  className={clsx(
                    'w-9 h-9 rounded-lg border-2 text-lg flex items-center justify-center transition-colors',
                    selectedIcon === icon
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                  )}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Cor
            </label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setValue('color', color.value)}
                  className={clsx(
                    'w-8 h-8 rounded-full border-2 transition-all',
                    selectedColor === color.value
                      ? 'border-gray-900 dark:border-white scale-110'
                      : 'border-transparent hover:scale-105'
                  )}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button type="submit" isLoading={isAdding} variant="outline">
            Adicionar conta
          </Button>
        </div>
      </form>

      {/* Navigation */}
      <div className="flex justify-between mt-8">
        <Button variant="ghost" onClick={onBack} leftIcon={<ChevronLeft size={18} />}>
          Voltar
        </Button>
        <Button
          onClick={onNext}
          disabled={accounts.length === 0}
          rightIcon={<ChevronRight size={18} />}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
};

const CategoryStep = ({ categoriesSeeded, onSeed, isSeeding, onNext, onBack }) => (
  <div className="max-w-lg mx-auto text-center">
    <div className="w-14 h-14 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mx-auto mb-4">
      <Tag size={28} className="text-primary-600" />
    </div>
    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
      Configure suas categorias
    </h2>
    <p className="text-gray-500 dark:text-gray-400 mb-8">
      Categorias ajudam a organizar suas receitas e despesas. Voce pode personaliza-las depois.
    </p>

    {categoriesSeeded ? (
      <div className="p-6 rounded-2xl bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 mb-8">
        <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
          <Check size={24} />
          <span className="text-lg font-semibold">Categorias criadas!</span>
        </div>
        <p className="text-sm text-green-700 dark:text-green-400">
          Criamos 10 categorias de despesas e 5 de receitas para voce. Personalize quando quiser em Categorias.
        </p>
      </div>
    ) : (
      <div className="space-y-4 mb-8">
        <div className="p-6 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
          <p className="font-medium text-gray-900 dark:text-white mb-3">
            Categorias padrao incluem:
          </p>
          <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm text-left">
            <div>
              <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Despesas:</p>
              <ul className="text-gray-500 dark:text-gray-400 space-y-0.5">
                <li>Alimentacao, Transporte</li>
                <li>Moradia, Saude, Educacao</li>
                <li>Lazer, Roupas, Assinaturas</li>
                <li>Impostos, Outros</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Receitas:</p>
              <ul className="text-gray-500 dark:text-gray-400 space-y-0.5">
                <li>Salario, Freelance</li>
                <li>Investimentos, Vendas</li>
                <li>Outros</li>
              </ul>
            </div>
          </div>
        </div>

        <Button size="lg" fullWidth onClick={onSeed} isLoading={isSeeding}>
          Criar categorias padrao
        </Button>
      </div>
    )}

    <div className="flex justify-between">
      <Button variant="ghost" onClick={onBack} leftIcon={<ChevronLeft size={18} />}>
        Voltar
      </Button>
      <Button
        onClick={onNext}
        disabled={!categoriesSeeded}
        rightIcon={<ChevronRight size={18} />}
      >
        Continuar
      </Button>
    </div>
  </div>
);

const TransactionStep = ({ accounts, categories, onSubmit, isSubmitting, onNext, onBack, transactionCreated }) => {
  const expenseCategories = categories
    .filter((c) => c.type === 'expense')
    .map((c) => ({ value: c.categoryId, label: c.name }));

  const incomeCategories = categories
    .filter((c) => c.type === 'income')
    .map((c) => ({ value: c.categoryId, label: c.name }));

  const accountOptions = accounts.map((a) => ({
    value: a.accountId,
    label: `${a.icon} ${a.name}`,
  }));

  const today = new Date().toISOString().split('T')[0];

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(transactionSchema),
    defaultValues: {
      description: '',
      amount: 0,
      type: 'expense',
      accountId: accounts[0]?.accountId || '',
      categoryId: '',
      date: today,
    },
  });

  const selectedType = watch('type');
  const categoryOptions = selectedType === 'income' ? incomeCategories : expenseCategories;

  if (transactionCreated) {
    return (
      <div className="max-w-lg mx-auto text-center">
        <div className="w-14 h-14 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
          <Check size={28} className="text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Transacao registrada!
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mb-8">
          Voce pode adicionar mais transacoes a qualquer momento na pagina de Transacoes.
        </p>
        <div className="flex justify-between">
          <Button variant="ghost" onClick={onBack} leftIcon={<ChevronLeft size={18} />}>
            Voltar
          </Button>
          <Button onClick={onNext} rightIcon={<ChevronRight size={18} />}>
            Finalizar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto w-full">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mx-auto mb-4">
          <Receipt size={28} className="text-primary-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Registre sua primeira transacao
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          Pode ser uma despesa recente ou sua ultima receita.
        </p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6"
      >
        {/* Type toggle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo</label>
          <div className="flex gap-2">
            {[
              { value: 'expense', label: 'Despesa', color: 'red' },
              { value: 'income', label: 'Receita', color: 'green' },
            ].map(({ value, label, color }) => (
              <label
                key={value}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border-2 cursor-pointer transition-all text-sm font-medium',
                  selectedType === value
                    ? color === 'red'
                      ? 'border-red-500 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                      : 'border-green-500 bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-gray-300'
                )}
              >
                <input type="radio" value={value} {...register('type')} className="sr-only" />
                {label}
              </label>
            ))}
          </div>
        </div>

        <Input
          label="Descricao"
          placeholder="Ex: Supermercado, Salario, etc"
          error={errors.description?.message}
          {...register('description')}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Controller
            name="amount"
            control={control}
            render={({ field }) => (
              <CurrencyInput
                label="Valor"
                value={field.value}
                onValueChange={(values) => field.onChange(values.floatValue ?? 0)}
                onBlur={field.onBlur}
                error={errors.amount?.message}
              />
            )}
          />
          <Input
            label="Data"
            type="date"
            error={errors.date?.message}
            {...register('date')}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Conta"
            options={accountOptions}
            error={errors.accountId?.message}
            {...register('accountId')}
          />
          <Select
            label="Categoria"
            options={categoryOptions}
            error={errors.categoryId?.message}
            {...register('categoryId')}
          />
        </div>

        <div className="flex justify-end pt-2">
          <Button type="submit" isLoading={isSubmitting}>
            Registrar transacao
          </Button>
        </div>
      </form>

      <div className="flex justify-between mt-8">
        <Button variant="ghost" onClick={onBack} leftIcon={<ChevronLeft size={18} />}>
          Voltar
        </Button>
        <Button variant="ghost" onClick={onNext} rightIcon={<ChevronRight size={18} />}>
          Pular e finalizar
        </Button>
      </div>
    </div>
  );
};

const DoneStep = ({ onFinish }) => (
  <div className="flex flex-col items-center text-center max-w-lg mx-auto">
    <div className="w-20 h-20 rounded-2xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-6">
      <Rocket size={40} className="text-green-600" />
    </div>

    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
      Tudo pronto!
    </h2>

    <p className="text-gray-500 dark:text-gray-400 text-lg mb-4">
      Sua conta esta configurada. Agora voce pode explorar todas as funcionalidades do MyBank.
    </p>

    <div className="w-full p-5 rounded-2xl bg-primary-50 dark:bg-primary-900/10 border border-primary-200 dark:border-primary-800 text-left mb-8">
      <p className="font-medium text-primary-900 dark:text-primary-300 mb-2">Proximos passos:</p>
      <ul className="text-sm text-primary-700 dark:text-primary-400 space-y-1.5">
        <li className="flex items-center gap-2">
          <ChevronRight size={14} /> Adicione mais contas e transacoes
        </li>
        <li className="flex items-center gap-2">
          <ChevronRight size={14} /> Defina orcamentos por categoria
        </li>
        <li className="flex items-center gap-2">
          <ChevronRight size={14} /> Configure recorrencias (salario, aluguel, assinaturas)
        </li>
        <li className="flex items-center gap-2">
          <ChevronRight size={14} /> Acompanhe seus relatorios no Dashboard
        </li>
      </ul>
    </div>

    <Button size="lg" onClick={onFinish} rightIcon={<ArrowRight size={18} />}>
      Ir para o Dashboard
    </Button>
  </div>
);

// --- Main Onboarding Page ---

const Onboarding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { completeOnboarding } = useOnboarding();

  const [currentStep, setCurrentStep] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoriesSeeded, setCategoriesSeeded] = useState(false);
  const [transactionCreated, setTransactionCreated] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if user already has data (e.g. returning after partial onboarding)
  useEffect(() => {
    const checkExistingData = async () => {
      try {
        const [existingAccounts, existingCategories] = await Promise.all([
          accountService.getAll(),
          categoryService.getAll(),
        ]);
        if (existingAccounts?.length > 0) {
          setAccounts(existingAccounts);
        }
        if (existingCategories?.length > 0) {
          setCategories(existingCategories);
          setCategoriesSeeded(true);
        }
      } catch {
        // Ignore - fresh user
      }
    };
    checkExistingData();
  }, []);

  const handleAddAccount = async (data) => {
    setIsAdding(true);
    try {
      const created = await accountService.create(data);
      setAccounts((prev) => [...prev, created]);
      toast.success(`Conta "${data.name}" criada!`);
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Erro ao criar conta');
    } finally {
      setIsAdding(false);
    }
  };

  const handleSeedCategories = async () => {
    setIsSeeding(true);
    try {
      await categoryService.seedDefaults();
      const cats = await categoryService.getAll();
      setCategories(cats || []);
      setCategoriesSeeded(true);
      toast.success('Categorias criadas com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Erro ao criar categorias');
    } finally {
      setIsSeeding(false);
    }
  };

  const handleCreateTransaction = async (data) => {
    setIsSubmitting(true);
    try {
      await transactionService.create(data);
      setTransactionCreated(true);
      toast.success('Transacao registrada!');
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Erro ao criar transacao');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinish = () => {
    completeOnboarding();
    navigate('/app/dashboard', { replace: true });
  };

  const goNext = () => setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setCurrentStep((s) => Math.max(s - 1, 0));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Progress bar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Passo {currentStep + 1} de {STEPS.length}
            </span>
            {currentStep < STEPS.length - 1 && (
              <button
                onClick={handleFinish}
                className="text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                Pular configuracao
              </button>
            )}
          </div>

          {/* Step indicators */}
          <div className="flex gap-1.5">
            {STEPS.map((step, i) => (
              <div
                key={step.id}
                className={clsx(
                  'h-1.5 flex-1 rounded-full transition-all duration-300',
                  i <= currentStep
                    ? 'bg-primary-600'
                    : 'bg-gray-200 dark:bg-gray-700'
                )}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        {currentStep === 0 && (
          <WelcomeStep user={user} onNext={goNext} />
        )}
        {currentStep === 1 && (
          <AccountStep
            accounts={accounts}
            onAddAccount={handleAddAccount}
            isAdding={isAdding}
            onNext={goNext}
            onBack={goBack}
          />
        )}
        {currentStep === 2 && (
          <CategoryStep
            categoriesSeeded={categoriesSeeded}
            onSeed={handleSeedCategories}
            isSeeding={isSeeding}
            onNext={goNext}
            onBack={goBack}
          />
        )}
        {currentStep === 3 && (
          <TransactionStep
            accounts={accounts}
            categories={categories}
            onSubmit={handleCreateTransaction}
            isSubmitting={isSubmitting}
            onNext={goNext}
            onBack={goBack}
            transactionCreated={transactionCreated}
          />
        )}
        {currentStep === 4 && (
          <DoneStep onFinish={handleFinish} />
        )}
      </div>
    </div>
  );
};

export default Onboarding;
